import React, {useMemo, useState} from 'react';

import {SignoffFooterPanel} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal/SignoffFooterPanel';
import {SignoffTeamsList} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal/SignoffTeamsList';
import {StatusPanel} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal/StatusPanel';
import {getCommonDocument} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal/utils';
import {useRequestSignoff, useSignoffTemplates} from 'app/signoffs/api';
import {UNLIMITED_PAGE_SIZE} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';

import type {BaseActionableDocumentType} from 'app/general/types';

type Props = {
  contentType: 'procedure' | 'policy' | 'proceduregroup';
  document: BaseActionableDocumentType;
  onClose: () => void;
  onSuccess: (doc?: BaseActionableDocumentType) => unknown;
};

export function SignoffTeams({
  document,
  contentType,
  onClose,
  onSuccess,
}: Props) {
  const [status, setStatus] = useState<'start' | 'success' | 'error'>('start');
  const [requestSignoff] = useRequestSignoff();

  const teamMemberships = getCommonDocument(document).team_memberships;
  const documentTeamIds = teamMemberships
    .map((t) => t.team_id)
    .sort()
    .join(',');

  const {data: signoffTemplates, isLoading} = useSignoffTemplates(
    {
      page: '1',
      page_size: UNLIMITED_PAGE_SIZE,
      ordering: 'team__name',

      team_ids: documentTeamIds,
    },
    {skip: teamMemberships.length === 0}
  );

  const signoffEnabledTeamUrls = useMemo(() => {
    if (signoffTemplates == null) {
      return new Set<string>();
    }
    return new Set(
      signoffTemplates.results
        .filter((template) => template.is_trigger_on_signoff_requested)
        .map((template) => template.team)
    );
  }, [signoffTemplates]);

  const generateSignoff = (e: React.MouseEvent) => {
    e.preventDefault();
    if (signoffEnabledTeamUrls.size === 0 || !document.can_edit) {
      return;
    }
    return requestSignoff({
      url: getCommonDocument(document).current_version.url,
      contentType,
    })
      .unwrap()
      .then(() => {
        onSuccess(document);
        onClose();
      })
      .catch((err) => {
        setStatus('error');
        return err;
      });
  };

  return (
    <>
      <h6 className="font-weight-bold text-uppercase text-secondary small mb-3">
        {gt('Require Sign Off for This Version')}
      </h6>
      {status === 'start' ? (
        <div className="mb-2">
          <StatusPanel
            isLoading={isLoading}
            document={document}
            disabled={signoffEnabledTeamUrls.size === 0}
          />
          {!isLoading ? (
            <SignoffTeamsList
              document={document}
              contentType={contentType}
              signoffEnabledTeamUrls={signoffEnabledTeamUrls}
            />
          ) : null}
        </div>
      ) : status === 'error' ? (
        <div className="alert alert-danger my-1">
          {gt('Request failed. Sign Offs were not requested.')}
        </div>
      ) : null}
      <SignoffFooterPanel
        status={status}
        onClose={onClose}
        disabled={signoffEnabledTeamUrls.size === 0 || !document.can_edit}
        disabledTooltip={gt('At least one team must have Sign Offs enabled.')}
        onSubmit={generateSignoff}
      />
    </>
  );
}
